import { Link } from 'react-router-dom';
import { useExpensesEdit } from '../hooks/EditExpenses';
import TopBar from '../../utils/TopBar';
import Footer from '../../utils/Footer';

const EditExpenses = () => {
  const {
    expenses,
    months,
    isModalOpen,
    newCategory,
    changesMade,
    handleCellChange,
    handleCategoryChange,
    handleSaveChanges,
    handleAddCategory,
    handleRemoveCategory,
    setIsModalOpen,
    setNewCategory
  } = useExpensesEdit();

  return (
    <div className="h-screen bg-gray-900 flex flex-col">
      <TopBar 
        title="Edit Expenses"
        subtitle="Modify your expense records"
      />

      <main className="flex-1 bg-gray-900 p-8 overflow-hidden flex flex-col">
        <div className="relative flex-1 overflow-auto">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-gray-800 z-20">
              <tr>
                <th className="sticky left-0 top-0 bg-gray-800 py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-blue-400 sm:pl-6 w-[250px] z-30">
                  Category
                </th>
                {months.map(month => (
                  <th key={month} className="px-3 py-3.5 text-right text-sm font-semibold text-blue-400 min-w-[120px]">
                    {month}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700 bg-gray-900">
              {expenses.map((row, index) => (
                <tr 
                  key={index} 
                  className={`group ${row.category === 'Total' ? 'sticky bottom-0 bg-[#1e2837] z-20' : 'hover:bg-[#1e2837]'}`}
                >
                  <td className={`whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6 sticky left-0 ${
                    row.category === 'Total'
                      ? 'bg-[#1e2837] text-blue-400 font-semibold z-20'
                      : 'bg-gray-900 group-hover:bg-[#1e2837]'
                  }`}>
                    <div className="flex items-center justify-between">
                      {row.category === 'Total' ? (
                        <span className="text-blue-400 font-semibold">Total</span>
                      ) : (
                        <>
                          <input
                            type="text"
                            value={row.category}
                            onChange={(e) => handleCategoryChange(index, e.target.value)}
                            className="bg-transparent text-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded w-full"
                          />
                          <button
                            onClick={() => handleRemoveCategory(row.category)}
                            className="ml-2 text-red-500 hover:text-red-400 focus:outline-none"
                          >
                            ×
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                  {months.map(month => (
                    <td key={month} className="whitespace-nowrap px-3 py-4 text-sm text-gray-300 text-right">
                      {row.category === 'Total' ? (
                        <span className="text-blue-400 font-semibold">
                          {parseFloat(String(row[month])).toLocaleString()}
                        </span>
                      ) : (
                        <input
                          type="text"
                          value={row[month]}
                          onChange={(e) => handleCellChange(index, month, e.target.value)}
                          className="bg-transparent text-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded text-right w-full"
                        />
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      <footer className="bg-gray-800 shadow-sm">
        <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 flex space-x-4">
          <button
            onClick={handleSaveChanges}
            className={`px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
              changesMade ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 cursor-not-allowed'
            }`}
            disabled={!changesMade}
          >
            Save Changes
          </button>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            Add New Category
          </button>
          <Link
            to="/expenses/dashboard"
            className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
          >
            Cancel
          </Link>
        </div>
      </footer>

      {isModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg leading-6 font-medium text-gray-900">Add New Category</h3>
                <div className="mt-2">
                  <input
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Enter new category name"
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleAddCategory}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Create New Category
                </button>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
              <Footer />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditExpenses;
